import { useState, useCallback, useMemo, useEffect } from "react";
import { StormDoorHandleType } from "../../../entities-generated/common/storm-door-handle-type";
import { IStormDoorHandleTypeOption } from "../../../entities-generated/common/storm-door-handle-type-option";
import { ILowesStormDoorHandleEditingModel, LowesStormDoorHandleEditingModel } from "../../../entities-generated/models/lowes-storm-door-handle/lowes-storm-door-handle-editing-model";
import { BwjInput } from "../../../framework/entities/bwj-input";
import { BwjValidityState } from "../../../framework/entities/bwj-validity-state";
import { EventNotificationService, useEventNotification } from "../../../framework/services/event-notification";
import { ImmutableFormValueService, ValidatedFormValueService, IFormValidityCheck } from "../../../framework/services/form-model-service-components";
import { NavigationService } from "../../../framework/services/navigation";
import { contentChanged, isNotNullOrWhitespace, isNullOrWhitespace } from "../../../framework/utils";
import { none, where } from "../../../framework/utils/collection";
import { LowesStormHandleEvents } from "../events";
import { IPublishHandleFormModel, IPublishHandleValueModel, IPublishHandleModelChange } from "./publish-handle-iface";
import { IPublishLowesStormDoorHandleRequest, PublishLowesStormDoorHandleRequest } from "../../../entities-generated/models/lowes-storm-door-handle/publish-lowes-storm-door-handle-request";

// ##BEGIN:DEVELOPERCODE##

// this is a good place for doing things like defining custom form value service types

// ##END:DEVELOPERCODE##

// %%BEGIN:id/immutable/string%%
export const id = new (class idService extends ImmutableFormValueService<ILowesStormDoorHandleEditingModel, string> {
    public initialize(data: ILowesStormDoorHandleEditingModel): void {
        this.value = data.id;
    }

    public applyToDataModel(data: ILowesStormDoorHandleEditingModel): void {
        data.id = this.value;
    }

    public getDefaultValue(): string {
        return '';
    }

    public getInputValue(): string {
        return this.value;
    }

})(
    'id' // id
    ,'' // description
);
// %%END:id/immutable/string%%

// %%BEGIN:description/validated/string/string%%
export const description = new (class descriptionService extends ValidatedFormValueService<ILowesStormDoorHandleEditingModel, string, string> {
    public initialize(data: ILowesStormDoorHandleEditingModel): void {
        this.setInputValue(new BwjInput<string>(data.description));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: ILowesStormDoorHandleEditingModel): void {
        data.description = this.value.value;
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.requiredFieldValidator(value, 'Enter handle description');
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'description' // id
    ,'Description' // description
);
// %%END:description/validated/string/string%%

// %%BEGIN:imageUrls/validated/string[]/string[]%%
export const imageUrls = new (class imageUrlsService extends ValidatedFormValueService<ILowesStormDoorHandleEditingModel, string[], string[]> {
    public initialize(data: ILowesStormDoorHandleEditingModel): void {
        this.setInputValue(new BwjInput<string[]>(data.imageUrls));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: ILowesStormDoorHandleEditingModel): void {
        data.imageUrls = this.value.value;
    }

    public getDefaultValue(): BwjInput<string[]> {
        return new BwjInput<string[]>([]);
    }

    public getInputValue(): BwjInput<string[]> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string[]>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string[] {
        return this.value.value;
    }

    public validate(value: string[]): Promise<BwjInput<string[]>> {
        const result = this.getInputObject(value);
        return Promise.resolve(result);
    }

    public onUpdate(value: string[]): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'imageUrls' // id
    ,'Supporting images' // description
);
// %%END:imageUrls/validated/string[]/string[]%%

// %%BEGIN:sellingPoints/validated/string/string[]%%
export const sellingPoints = new (class sellingPointsService extends ValidatedFormValueService<ILowesStormDoorHandleEditingModel, string, string[]> {
    public initialize(data: ILowesStormDoorHandleEditingModel): void {
        this.setInputValue(new BwjInput<string>(data.sellingPoints.join('\n')));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: ILowesStormDoorHandleEditingModel): void {
        data.sellingPoints = this.value.value.split('\n');
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string[] {
        const value = this.value.value;
        if(isNullOrWhitespace(value)) { return []; }

        const values = value.split('\n');
        var outValue = where(values, v => isNotNullOrWhitespace(v));
        return outValue;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.getInputObject(value);
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'sellingPoints' // id
    ,'Selling points' // description
);
// %%END:sellingPoints/validated/string/string[]%%

// %%BEGIN:handleType/validated/IStormDoorHandleTypeOption | null/StormDoorHandleType%%
export const handleType = new (class handleTypeService extends ValidatedFormValueService<ILowesStormDoorHandleEditingModel, IStormDoorHandleTypeOption | null, StormDoorHandleType> {
    public initialize(data: ILowesStormDoorHandleEditingModel): void {
        this.options = data.doorHandleTypes;
        this.setInputValue(new BwjInput<IStormDoorHandleTypeOption | null>(null));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: ILowesStormDoorHandleEditingModel): void {}

    public getDefaultValue(): BwjInput<IStormDoorHandleTypeOption | null> {
        return new BwjInput<IStormDoorHandleTypeOption | null>(null);
    }

    public getInputValue(): BwjInput<IStormDoorHandleTypeOption | null> {
        return this.value;
    }

    public setInputValue(value: BwjInput<IStormDoorHandleTypeOption | null>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): StormDoorHandleType {
        const value = this.value.value;
        return value?.id ?? StormDoorHandleType.Indeterminate;
    }

    public validate(value: IStormDoorHandleTypeOption | null): Promise<BwjInput<IStormDoorHandleTypeOption | null>> {
        const result = this.requiredFieldValidator(value, 'Select a handle type');
        return Promise.resolve(result);
    }

    public onUpdate(value: IStormDoorHandleTypeOption | null): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

    public options: IStormDoorHandleTypeOption[] = [];
})(
    'handleType' // id
    ,'Handle type' // description
);
// %%END:handleType/validated/IStormDoorHandleTypeOption | null/StormDoorHandleType%%

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toFormModel = (): IPublishHandleFormModel => {
    return {
       id: id.getInputValue(),
       description: description.getInputValue(),
       imageUrls: imageUrls.getInputValue(),
       sellingPoints: sellingPoints.getInputValue(),
       handleType: handleType.getInputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toValueModel = (): IPublishHandleValueModel => {
    return {
       description: description.getOutputValue(),
       imageUrls: imageUrls.getOutputValue(),
       sellingPoints: sellingPoints.getOutputValue(),
       handleType: handleType.getOutputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const getValidatedInputs = () => {
    return [
        description.getInputValue(),
        imageUrls.getInputValue(),
        sellingPoints.getInputValue(),
        handleType.getInputValue(),
    ];
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isFormValid = () => none(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const getFormErrors = () => where(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const broadcastChange = (propertyName: string) => {
    const arg: IPublishHandleModelChange = {
        propertyChanged: propertyName,
        model: toFormModel()
    };

    EventNotificationService.dispatch(LowesStormHandleEvents.PublishFormModelDidUpdate, arg);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const validateAndUpdateModel = (): Promise<IFormValidityCheck<IPublishHandleFormModel>> => {
    const promises = [
        description.formValidateAndUpdate(),
        imageUrls.formValidateAndUpdate(),
        sellingPoints.formValidateAndUpdate(),
        handleType.formValidateAndUpdate(),
    ];

    return Promise.all(promises)
        .then(() => { 
            broadcastChange('validateAndUpdateModel:IPublishHandleFormModel');
            return { isValid: isFormValid(), model: toFormModel() };
        });
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isModelChanged = () => {
    if(initialState.valueModel === undefined) { return false; }
    const current = toValueModel();
    return contentChanged(initialState.valueModel, current);
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const initialState: { valueModel?: IPublishHandleValueModel, dataModel: ILowesStormDoorHandleEditingModel } = {
    dataModel: new LowesStormDoorHandleEditingModel()
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const initializeService = (data?: ILowesStormDoorHandleEditingModel) => {
    const dataModel = data || new LowesStormDoorHandleEditingModel();

    id.initialize(dataModel);
    description.initialize(dataModel);
    imageUrls.initialize(dataModel);
    sellingPoints.initialize(dataModel);
    handleType.initialize(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
    broadcastChange('initializeService:IPublishHandleFormModel');
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
description.setChangePublisher(broadcastChange);
imageUrls.setChangePublisher(broadcastChange);
sellingPoints.setChangePublisher(broadcastChange);
handleType.setChangePublisher(broadcastChange);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const setReversionState = () => {
    const dataModel = new LowesStormDoorHandleEditingModel();

    id.applyToDataModel(dataModel);
    description.applyToDataModel(dataModel);
    imageUrls.applyToDataModel(dataModel);
    sellingPoints.applyToDataModel(dataModel);
    handleType.applyToDataModel(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const revertChanges = () => {
    initializeService(initialState.dataModel);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModelChangeEvent = (onChangeWrappedInUseCallback: ((change: IPublishHandleModelChange) => void), subscriberId: string) =>
    useEventNotification(onChangeWrappedInUseCallback, [onChangeWrappedInUseCallback], LowesStormHandleEvents.PublishFormModelDidUpdate, subscriberId);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModel = (subscriberId: string): IPublishHandleFormModel => {
    const [getter, setter] = useState<IPublishHandleFormModel>(toFormModel());

    useFormModelChangeEvent(useCallback((change: IPublishHandleModelChange) => setter(change.model), []), subscriberId);

    return getter;
}
// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useHasUnsavedChanges = (model: IPublishHandleFormModel, warnOnUnsavedPageNavigation: boolean = false) => {
    const hasUnsavedChanges = useMemo(isModelChanged, [model]);

    useEffect(() => {
        if(warnOnUnsavedPageNavigation === false || hasUnsavedChanges === false) { return; }

        const unloadCallback = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', unloadCallback);
        NavigationService.registerObjection('PublishHandleFormService');

        return () => {
            window.removeEventListener('beforeunload', unloadCallback);
            NavigationService.rescindObjection('PublishHandleFormService');
        }
    }, [warnOnUnsavedPageNavigation, hasUnsavedChanges]);

    return hasUnsavedChanges;
}
// ##BEGIN:DEVELOPERCODE##

export const toPublishRequest = () => {
    const v = toValueModel();
    const request: IPublishLowesStormDoorHandleRequest = {
        description: v.description,
        imageUrls: v.imageUrls,
        sellingPoints: v.sellingPoints,
        isRejected: false,
        handleType: v.handleType
    };
    return request;
}

export const toPublishRejectionRequest = () => {
    const request = new PublishLowesStormDoorHandleRequest();
    request.isRejected = true;
    return request;
}

// ##END:DEVELOPERCODE##
